/**
 * Cliente de condições marítimas — fontes GRATUITAS e SEM CHAVE:
 *  - Open-Meteo Forecast (clima): vento, rajada, pressão, umidade, chuva, UV, sol.
 *  - Open-Meteo Marine (mar): ondas, período, direção, temperatura do mar, swell.
 *  - Solunar.org: fases da lua, períodos de pesca (maior/menor), nascer/pôr lua.
 *
 * Todas com CORS liberado (uso direto do navegador). Os dados são buscados na
 * entrada do app e cacheados (ver stores/conditions.ts) para economizar chamadas.
 */

const FORECAST_URL = 'https://api.open-meteo.com/v1/forecast';
const MARINE_URL = 'https://marine-api.open-meteo.com/v1/marine';
const SOLUNAR_URL = 'https://api.solunar.org/solunar';

const COMPASS_16 = [
  'N', 'NNE', 'NE', 'ENE', 'L', 'ESE', 'SE', 'SSE',
  'S', 'SSO', 'SO', 'OSO', 'O', 'ONO', 'NO', 'NNO',
];

export function degToCompass(deg: number | null | undefined): string | null {
  if (deg == null) return null;
  return COMPASS_16[Math.round(((deg % 360) / 22.5)) % 16];
}

export interface WeatherNow {
  temp: number | null;
  apparentTemp: number | null;
  humidity: number | null;
  pressure: number | null;
  precipitation: number | null;
  weatherCode: number | null;
  isDay: boolean;
  windKn: number | null;
  gustKn: number | null;
  windDirDeg: number | null;
  visibilityM: number | null;
}

export interface MarineNow {
  waveHeight: number | null;
  waveDirDeg: number | null;
  wavePeriod: number | null;
  swellHeight: number | null;
  windWaveHeight: number | null;
  seaTemp: number | null;
}

export interface DayForecast {
  date: string;
  weatherCode: number | null;
  tempMax: number | null;
  tempMin: number | null;
  sunrise: string | null;
  sunset: string | null;
  uvMax: number | null;
  precipSum: number | null;
  precipProb: number | null;
  windMaxKn: number | null;
  gustMaxKn: number | null;
  windDirDeg: number | null;
  waveMax: number | null;
  waveDirDeg: number | null;
  wavePeriodMax: number | null;
}

export interface SolunarPeriod {
  start: string;
  stop: string;
}

export interface SolunarData {
  moonPhase: string | null;
  moonIllumination: number | null;
  moonRise: string | null;
  moonSet: string | null;
  sunRise: string | null;
  sunSet: string | null;
  major: SolunarPeriod[];
  minor: SolunarPeriod[];
  dayRating: number | null; // 0–4 (qualidade do dia p/ pesca)
}

export interface CptecWave {
  periodo: string;
  agitacao: string | null;
  altura: number | null;
  direcao: string | null;
  vento: number | null;
  vento_dir: string | null;
}

export interface CptecDay {
  dia: string | null;
  tempo: string | null;
  maxima: number | null;
  minima: number | null;
  iuv: number | null;
}

export interface InmetObs {
  estacao: string | null;
  distancia_km: number | null;
  temp?: number | null;
  umidade?: number | null;
  pressao?: number | null;
  vento_vel_ms?: number | null;
  vento_dir?: number | null;
  chuva_mm?: number | null;
  data?: string | null;
  hora_utc?: string | null;
}

export interface BrSources {
  municipio: string | null;
  uf: string | null;
  cptec: { cidade: string; uf: string; previsao: CptecDay[]; ondas: CptecWave[] } | null;
  inmet: InmetObs | null;
}

export interface MarineConditions {
  weather: WeatherNow | null;
  marine: MarineNow | null;
  days: DayForecast[];
  solunar: SolunarData | null;
  br: BrSources | null;
  place: string | null;
}

/** Códigos de tempo do CPTEC/INPE → texto legível (os mais comuns). */
const CPTEC_TEMPO: Record<string, string> = {
  ec: 'Encoberto c/ chuvas', ci: 'Chuvas isoladas', c: 'Chuva', in: 'Instável',
  pp: 'Poss. pancadas', cm: 'Chuva pela manhã', cn: 'Chuva à noite',
  pt: 'Pancadas à tarde', pm: 'Pancadas pela manhã', np: 'Nublado e pancadas',
  pc: 'Pancadas de chuva', pn: 'Parcialmente nublado', cv: 'Chuvisco',
  ch: 'Chuvoso', t: 'Tempestade', ps: 'Predomínio de sol', e: 'Encoberto',
  n: 'Nublado', cl: 'Céu claro', nv: 'Nevoeiro', g: 'Geada', ne: 'Neve',
  nd: 'Não definido', vn: 'Variação de nebulosidade',
};

export function cptecTempoLabel(code: string | null): string | null {
  if (!code) return null;
  return CPTEC_TEMPO[code] ?? code;
}

const BR_SOURCES_URL = '/geo/v1/marine/br';

export async function fetchBrSources(
  lat: number,
  lon: number,
  signal?: AbortSignal,
): Promise<BrSources | null> {
  try {
    return (await getJson(`${BR_SOURCES_URL}?lat=${lat}&lon=${lon}`, signal)) as BrSources;
  } catch {
    return null; // /geo não exposto (dev) ou fontes BR fora do ar
  }
}

const num = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null;

async function getJson(url: string, signal?: AbortSignal): Promise<any> {
  const r = await fetch(url, { signal });
  if (!r.ok) throw new Error(`${r.status}`);
  return r.json();
}

/** Combina o signal externo com um timeout local — evita que uma API lenta/fora do ar
 * (ex.: solunar.org com ERR_CONNECTION_TIMED_OUT) trave o restante das condições. */
function withTimeout(signal: AbortSignal | undefined, ms: number): AbortSignal {
  const ctrl = new AbortController();
  setTimeout(() => ctrl.abort(), ms);
  if (signal) {
    if (signal.aborted) ctrl.abort();
    else signal.addEventListener('abort', () => ctrl.abort(), { once: true });
  }
  return ctrl.signal;
}

async function fetchWeather(lat: number, lon: number, signal?: AbortSignal) {
  const p = new URLSearchParams({
    latitude: String(lat),
    longitude: String(lon),
    current:
      'temperature_2m,relative_humidity_2m,apparent_temperature,is_day,precipitation,weather_code,pressure_msl,wind_speed_10m,wind_direction_10m,wind_gusts_10m,visibility',
    daily:
      'weather_code,temperature_2m_max,temperature_2m_min,sunrise,sunset,uv_index_max,precipitation_sum,precipitation_probability_max,wind_speed_10m_max,wind_gusts_10m_max,wind_direction_10m_dominant',
    wind_speed_unit: 'kn',
    timezone: 'auto',
    forecast_days: '3',
  });
  return getJson(`${FORECAST_URL}?${p}`, signal);
}

async function fetchMarine(lat: number, lon: number, signal?: AbortSignal) {
  const p = new URLSearchParams({
    latitude: String(lat),
    longitude: String(lon),
    current:
      'wave_height,wave_direction,wave_period,sea_surface_temperature,swell_wave_height,wind_wave_height',
    daily: 'wave_height_max,wave_direction_dominant,wave_period_max',
    timezone: 'auto',
    forecast_days: '3',
  });
  try {
    return await getJson(`${MARINE_URL}?${p}`, signal);
  } catch {
    return null; // ponto em terra / sem cobertura marítima
  }
}

async function fetchSolunar(lat: number, lon: number, signal?: AbortSignal): Promise<SolunarData | null> {
  const now = new Date();
  const date = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const tz = -Math.round(now.getTimezoneOffset() / 60); // ex.: Brasil = -3
  try {
    const d = await getJson(
      `${SOLUNAR_URL}/${lat.toFixed(4)},${lon.toFixed(4)},${date},${tz}`,
      withTimeout(signal, 7000),
    );
    const periods = (a?: string, b?: string): SolunarPeriod[] =>
      a && b ? [{ start: a, stop: b }] : [];
    return {
      moonPhase: d.moonPhase ?? null,
      moonIllumination: num(d.moonIllumination),
      moonRise: d.moonRise ?? null,
      moonSet: d.moonSet ?? null,
      sunRise: d.sunRise ?? null,
      sunSet: d.sunSet ?? null,
      major: [
        ...periods(d.major1Start, d.major1Stop),
        ...periods(d.major2Start, d.major2Stop),
      ],
      minor: [
        ...periods(d.minor1Start, d.minor1Stop),
        ...periods(d.minor2Start, d.minor2Stop),
      ],
      dayRating: num(d.dayRating),
    };
  } catch {
    return null;
  }
}

const MOON_PT: Record<string, string> = {
  'New Moon': 'Lua Nova',
  'Waxing Crescent': 'Crescente côncava',
  'First Quarter': 'Quarto Crescente',
  'Waxing Gibbous': 'Crescente gibosa',
  'Full Moon': 'Lua Cheia',
  'Waning Gibbous': 'Minguante gibosa',
  'Last Quarter': 'Quarto Minguante',
  'Waning Crescent': 'Minguante côncava',
};

export function moonPhasePt(phase: string | null): string | null {
  if (!phase) return null;
  return MOON_PT[phase] ?? phase;
}

/** Fase da lua + iluminação calculadas localmente (independe de API externa). */
const SYNODIC = 29.53058867;
function computeMoon(date: Date): { phase: string; illumination: number } {
  const jd = date.getTime() / 86400000 + 2440587.5;
  const knownNewJD = Date.UTC(2000, 0, 6, 18, 14) / 86400000 + 2440587.5;
  let age = (jd - knownNewJD) % SYNODIC;
  if (age < 0) age += SYNODIC;
  const names = [
    'New Moon', 'Waxing Crescent', 'First Quarter', 'Waxing Gibbous',
    'Full Moon', 'Waning Gibbous', 'Last Quarter', 'Waning Crescent',
  ];
  // bordas estreitas (~1.85d) para as 4 fases "exatas".
  let idx: number;
  if (age < 1.85) idx = 0;
  else if (age < 5.53) idx = 1;
  else if (age < 9.22) idx = 2;
  else if (age < 12.91) idx = 3;
  else if (age < 16.61) idx = 4;
  else if (age < 20.30) idx = 5;
  else if (age < 23.99) idx = 6;
  else if (age < 27.68) idx = 7;
  else idx = 0;
  const illumination = (1 - Math.cos((2 * Math.PI * age) / SYNODIC)) / 2;
  return { phase: names[idx], illumination };
}

function hhmm(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const t = iso.includes('T') ? iso.split('T')[1] : iso;
  return t ? t.slice(0, 5) : null;
}

export async function fetchMarineConditions(
  lat: number,
  lon: number,
  place: string | null,
  signal?: AbortSignal,
): Promise<MarineConditions> {
  const [w, m, solunarRaw, br] = await Promise.all([
    fetchWeather(lat, lon, signal),
    fetchMarine(lat, lon, signal),
    fetchSolunar(lat, lon, signal),
    fetchBrSources(lat, lon, signal),
  ]);

  const c = w?.current ?? {};
  const weather: WeatherNow = {
    temp: num(c.temperature_2m),
    apparentTemp: num(c.apparent_temperature),
    humidity: num(c.relative_humidity_2m),
    pressure: num(c.pressure_msl),
    precipitation: num(c.precipitation),
    weatherCode: num(c.weather_code),
    isDay: c.is_day === 1,
    windKn: num(c.wind_speed_10m),
    gustKn: num(c.wind_gusts_10m),
    windDirDeg: num(c.wind_direction_10m),
    visibilityM: num(c.visibility),
  };

  const mc = m?.current ?? null;
  const marine: MarineNow | null = mc
    ? {
        waveHeight: num(mc.wave_height),
        waveDirDeg: num(mc.wave_direction),
        wavePeriod: num(mc.wave_period),
        swellHeight: num(mc.swell_wave_height),
        windWaveHeight: num(mc.wind_wave_height),
        seaTemp: num(mc.sea_surface_temperature),
      }
    : null;

  const wd = w?.daily;
  const md = m?.daily;
  const days: DayForecast[] = wd?.time
    ? wd.time.map((t: string, i: number) => ({
        date: t,
        weatherCode: num(wd.weather_code?.[i]),
        tempMax: num(wd.temperature_2m_max?.[i]),
        tempMin: num(wd.temperature_2m_min?.[i]),
        sunrise: wd.sunrise?.[i] ?? null,
        sunset: wd.sunset?.[i] ?? null,
        uvMax: num(wd.uv_index_max?.[i]),
        precipSum: num(wd.precipitation_sum?.[i]),
        precipProb: num(wd.precipitation_probability_max?.[i]),
        windMaxKn: num(wd.wind_speed_10m_max?.[i]),
        gustMaxKn: num(wd.wind_gusts_10m_max?.[i]),
        windDirDeg: num(wd.wind_direction_10m_dominant?.[i]),
        waveMax: num(md?.wave_height_max?.[i]),
        waveDirDeg: num(md?.wave_direction_dominant?.[i]),
        wavePeriodMax: num(md?.wave_period_max?.[i]),
      }))
    : [];

  // Solunar resiliente: sol vem do Open-Meteo (confiável) e a lua é calculada
  // localmente; períodos maior/menor só quando a API solunar responde.
  const moon = computeMoon(new Date());
  const solunar: SolunarData = {
    moonPhase: solunarRaw?.moonPhase ?? moon.phase,
    moonIllumination: solunarRaw?.moonIllumination ?? moon.illumination,
    moonRise: solunarRaw?.moonRise ?? null,
    moonSet: solunarRaw?.moonSet ?? null,
    sunRise: solunarRaw?.sunRise ?? hhmm(days[0]?.sunrise),
    sunSet: solunarRaw?.sunSet ?? hhmm(days[0]?.sunset),
    major: solunarRaw?.major ?? [],
    minor: solunarRaw?.minor ?? [],
    dayRating: solunarRaw?.dayRating ?? null,
  };

  return { weather, marine, days, solunar, br, place };
}

/**
 * Tipo de corpo d'água: 'mar' (oceano/costa/baía) ou 'interior' (represa, rio,
 * lago, lagoa, açude). Define quais métricas/alertas fazem sentido. Usa o nome
 * do local (palavras-chave) e, como reforço, a presença de dados de onda da
 * Open-Meteo Marine (que só cobre oceano).
 */
export type WaterBody = 'mar' | 'interior';

const INTERIOR_RE =
  /\b(represa|barragem|hidrel[ée]tric|usina|uhe|lago|lagoa|lagao|rio|riacho|a[çc]ude|reservat[óo]rio|igarap[ée]|c[óo]rrego|canal|eclusa)\b/i;
const MAR_RE = /\b(mar|oceano|praia|ba[íi]a|enseada|porto|marina|farol|costa|p[íi]er|cais|ilha)\b/i;

export function classifyWaterBody(place: string | null, marine: MarineNow | null): WaterBody {
  const p = place ?? '';
  if (INTERIOR_RE.test(p)) return 'interior';
  if (MAR_RE.test(p)) return 'mar';
  // Sem pista no nome: se a API marítima não trouxe ondas, tratamos como interior.
  return marine?.waveHeight != null ? 'mar' : 'interior';
}

export function waterBodyLabel(body: WaterBody): string {
  return body === 'mar' ? 'Mar / costa' : 'Águas interiores';
}

/** Códigos WMO de tempestade/chuva forte (Open-Meteo weather_code). */
function stormSeverity(code: number | null): SafetyLevel | null {
  if (code == null) return null;
  if ([95, 96, 99].includes(code)) return 'critico'; // trovoadas (raio)
  if ([65, 67, 82].includes(code)) return 'critico'; // chuva/aguaceiro violento
  if ([63, 81, 75, 86].includes(code)) return 'atencao'; // chuva/neve moderada a forte
  return null;
}

/** Nível de segurança de navegação. Regra adapta-se ao corpo d'água. */
export type SafetyLevel = 'ok' | 'atencao' | 'critico';

export function safetyLevel(
  weather: WeatherNow | null,
  marine: MarineNow | null,
  body: WaterBody = 'mar',
): {
  level: SafetyLevel;
  reasons: string[];
} {
  const reasons: string[] = [];
  let level: SafetyLevel = 'ok';
  const bump = (l: SafetyLevel) => {
    const order = { ok: 0, atencao: 1, critico: 2 } as const;
    if (order[l] > order[level]) level = l;
  };

  const gust = weather?.gustKn ?? weather?.windKn ?? null;
  if (gust != null) {
    // Em águas interiores, embarcações pequenas viram com menos vento → limiar menor.
    const crit = body === 'interior' ? 28 : 33;
    const aten = body === 'interior' ? 18 : 22;
    if (gust >= crit) { bump('critico'); reasons.push(`Vento forte (rajadas ${Math.round(gust)} nós)`); }
    else if (gust >= aten) { bump('atencao'); reasons.push(`Vento moderado (rajadas ${Math.round(gust)} nós)`); }
  }

  // Ondas só entram na régua quando há mar (oceano/costa).
  if (body === 'mar') {
    const wave = marine?.waveHeight ?? null;
    if (wave != null) {
      if (wave >= 2.5) { bump('critico'); reasons.push(`Mar grosso (ondas ${wave.toFixed(1)} m)`); }
      else if (wave >= 1.25) { bump('atencao'); reasons.push(`Mar moderado (ondas ${wave.toFixed(1)} m)`); }
    }
  }

  const storm = stormSeverity(weather?.weatherCode ?? null);
  if (storm) { bump(storm); reasons.push(storm === 'critico' ? 'Tempestade / trovoadas (risco de raios)' : 'Chuva forte na região'); }

  const vis = weather?.visibilityM ?? null;
  if (vis != null && vis < 1000) { bump('critico'); reasons.push('Visibilidade baixa (< 1 km)'); }
  else if (vis != null && vis < 4000) { bump('atencao'); reasons.push('Visibilidade reduzida'); }

  if (reasons.length === 0) reasons.push('Condições favoráveis para navegação');
  return { level, reasons };
}
